import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  generateAstrologerDiagnosticDossier,
  AstrologerDiagnosticReport
} from '../utils/astrologerDiagnosticEngine';
import {
  Compass,
  Sparkles,
  Shield,
  AlertTriangle,
  Award,
  Zap,
  Clock,
  Flame,
  Droplets,
  Wind,
  Mountain,
  Heart,
  Briefcase,
  Coins,
  Baby,
  Eye,
  CheckCircle2,
  HelpCircle,
  Maximize2,
  Layers,
  ChevronRight,
  TrendingUp,
  Activity,
  UserCheck
} from 'lucide-react';

interface AstrologerDiagnosticDossierProps {
  kundali: CompleteKundali;
  lang?: Language;
  onOpenSection?: (section: string) => void;
}

export const AstrologerDiagnosticDossier: React.FC<AstrologerDiagnosticDossierProps> = ({
  kundali,
  lang = 'en',
  onOpenSection
}) => {
  const isHi = lang === 'hi';
  const [activePhase, setActivePhase] = useState<'all' | 'phase1' | 'phase2' | 'phase3' | 'phase4'>('all');

  const dossier: AstrologerDiagnosticReport = useMemo(() => {
    return generateAstrologerDiagnosticDossier(kundali);
  }, [kundali]);

  const {
    threePillars,
    functionalClassification,
    kendraTrikonaScore,
    vulnerabilities,
    auspiciousMarks,
    neechaBhanga,
    jaiminiKarakas,
    kartari,
    d1toD9Shifts,
    specialLagnas,
    doubleTransitRadar,
    activeDashaResonance,
    karmaDiagnostic,
    consultationBrief
  } = dossier;

  const getElementIcon = (elementEn: string) => {
    switch (elementEn.toLowerCase()) {
      case 'fire':
        return <Flame className="w-3.5 h-3.5 text-amber-500" />;
      case 'water':
        return <Droplets className="w-3.5 h-3.5 text-blue-500" />;
      case 'air':
        return <Wind className="w-3.5 h-3.5 text-sky-500" />;
      default:
        return <Mountain className="w-3.5 h-3.5 text-emerald-600" />;
    }
  };

  const getRoleBadge = (role: string, isBadhakesh: boolean) => {
    if (role === 'yogakaraka') {
      return (
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white shadow-2xs">
          {isHi ? 'योगकारक' : 'Yogakaraka'}
        </span>
      );
    }
    if (isBadhakesh) {
      return (
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-purple-600 text-white shadow-2xs">
          {isHi ? 'बाधक' : 'Badhakesh'}
        </span>
      );
    }
    if (role === 'benefic') {
      return (
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white shadow-2xs">
          {isHi ? 'कारक शुभ' : 'F. Benefic'}
        </span>
      );
    }
    if (role === 'maraka') {
      return (
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-600 text-white shadow-2xs">
          {isHi ? 'मारक' : 'Maraka'}
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-600 text-white shadow-2xs">
        {isHi ? 'पाप/बाधक' : 'F. Malefic'}
      </span>
    );
  };

  return (
    <div id="astrologer-diagnostic-dossier" className="space-y-6 animate-fadeIn pb-12">
      {/* 1. TOP BANNER & PHASE TOGGLE */}
      <div className="bg-radial from-amber-900/10 via-astro-card to-astro-bg border border-astro-border rounded-3xl p-5 sm:p-7 shadow-xs relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                {isHi ? 'पारम्परिक गुरु-शिष्य निदान पद्धति' : 'Master Jyotishi Diagnostic Protocol'}
              </span>
              <span className="text-xs text-astro-secondary-text font-serif">
                {isHi ? 'चार-चरणीय वैज्ञानिक विश्लेषण' : '4-Tier Classical Analytical Framework'}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary flex items-center gap-2">
              {isHi ? 'ज्योतिषी निदान डॉसियर (पूर्ण कुंडली परीक्षण)' : "Astrologer's Diagnostic Dossier"}
            </h2>
            <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed">
              {isHi
                ? 'वरिष्ठ ज्योतिषी द्वारा कुंडली देखने की शास्त्रीय प्रक्रिया: त्रि-स्तम्भ (लग्न-चंद्र-सूर्य), योगकारक-बाधक, नवांश पुष्टि, चर कारक, और शनि-गुरु का संयुक्त गोचर।'
                : 'The systematic top-down methodology used by professional astrologers: Three Pillars, Functional Dignities, Navamsha Reality Verification, Jaimini Karakas, and Double Transit triggers.'}
            </p>
          </div>

          {/* Phase Filter Tabs */}
          <div className="flex items-center gap-1.5 bg-astro-bg border border-astro-border p-1.5 rounded-2xl flex-wrap">
            <button
              onClick={() => setActivePhase('all')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activePhase === 'all'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'समग्र डॉसियर' : 'Full Dossier'}
            </button>
            <button
              onClick={() => setActivePhase('phase1')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activePhase === 'phase1'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? '१. त्रि-स्तम्भ' : '1. Three Pillars'}
            </button>
            <button
              onClick={() => setActivePhase('phase2')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activePhase === 'phase2'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? '२. चर कारक व कर्तरी' : '2. Karakas & Kartari'}
            </button>
            <button
              onClick={() => setActivePhase('phase3')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activePhase === 'phase3'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? '३. नवांश व इन्दु लग्न' : '3. D9 Shift & Wealth'}
            </button>
            <button
              onClick={() => setActivePhase('phase4')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activePhase === 'phase4'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? '४. संयुक्त गोचर व दशा' : '4. Double Transit'}
            </button>
          </div>
        </div>
      </div>

      {/* 2. EXECUTIVE CONSULTATION BRIEF ("The First 2 Minutes") */}
      <div className="bg-amber-50/50 dark:bg-amber-950/20 border-2 border-amber-500/30 rounded-3xl p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-amber-500/20 pb-3">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-amber-700 dark:text-amber-400" />
            <h3 className="font-serif font-bold text-base text-amber-900 dark:text-amber-200">
              {isHi ? 'परामर्श सारांश: प्रथम २ मिनट का निष्कर्ष' : 'Executive Consultation Brief: First 2 Minutes'}
            </h3>
          </div>
          <span className="text-[11px] font-bold text-amber-800 dark:text-amber-300 bg-amber-500/10 px-2.5 py-1 rounded-full border border-amber-500/20">
            {karmaDiagnostic.titleEn}
          </span>
        </div>

        <p className="text-xs sm:text-sm text-astro-text leading-relaxed font-medium">
          {isHi ? consultationBrief.first60SecondsTakeawayHi : consultationBrief.first60SecondsTakeawayEn}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          {/* Primary Blessing */}
          <div className="bg-astro-bg/80 border border-emerald-500/30 rounded-2xl p-3.5 flex items-start gap-3">
            <Award className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                {isHi ? 'मुख्य दैवीय वरदान' : 'Primary Divine Blessing'}
              </span>
              <p className="text-xs text-astro-text leading-snug">
                {isHi ? consultationBrief.primaryBlessingHi : consultationBrief.primaryBlessingEn}
              </p>
            </div>
          </div>

          {/* Critical Warning / Karmic Lesson */}
          <div className="bg-astro-bg/80 border border-rose-500/30 rounded-2xl p-3.5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400">
                {isHi ? 'सावधानी व मुख्य परीक्षा' : 'Critical Warning / Karmic Friction'}
              </span>
              <p className="text-xs text-astro-text leading-snug">
                {isHi
                  ? (consultationBrief.criticalWarningHi || 'कोई गंभीर गंडांत या मृत्यु भाग नहीं। कुंडली में संतुलन है।')
                  : (consultationBrief.criticalWarningEn || 'No critical Gandanta or Mrityu Bhaga found. Normal balance.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* PHASE 1: THE FIRST 60 SECONDS (Three Pillars & Functional Roles)   */}
      {/* =================================================================== */}
      {(activePhase === 'all' || activePhase === 'phase1') && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-astro-border pb-2">
            <Compass className="w-5 h-5 text-astro-accent" />
            <h3 className="font-serif font-bold text-lg text-astro-text">
              {isHi ? 'प्रथम चरण: त्रि-स्तम्भ एवं कार्यात्मक प्रकृति' : 'Phase 1: The Three Pillars & Functional Dignities'}
            </h3>
          </div>

          {/* 3 Pillars Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Lagna (Ascendant) */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-4 shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  {isHi ? '१. लग्न (शरीर व जीवन शक्ति)' : '1. Ascendant (Physical Vessel)'}
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1 ${threePillars.lagna.element.color}`}>
                  {getElementIcon(threePillars.lagna.element.en)}
                  {isHi ? threePillars.lagna.element.hi : threePillars.lagna.element.en}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-astro-primary font-serif">
                  {threePillars.lagna.sign} ({threePillars.lagna.degreeFormatted})
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi ? 'स्वामी:' : 'Lord:'} <strong>{threePillars.lagna.lord}</strong> • {isHi ? 'नक्षत्र:' : 'Nakshatra:'} {threePillars.lagna.nakshatra} (प/P{threePillars.lagna.pada})
                </p>
              </div>
              <p className="text-xs text-astro-text/90 leading-relaxed border-t border-astro-border/50 pt-2">
                {threePillars.lagna.houseLordshipVerdict}
              </p>
            </div>

            {/* Moon (Chandra) */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-4 shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  {isHi ? '२. चंद्र (मन व दृष्टिकोण)' : '2. Chandra (Mind & Perception)'}
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1 ${threePillars.chandra.element.color}`}>
                  {getElementIcon(threePillars.chandra.element.en)}
                  {isHi ? threePillars.chandra.element.hi : threePillars.chandra.element.en}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-astro-primary font-serif">
                  {threePillars.chandra.sign} ({threePillars.chandra.degreeFormatted})
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {threePillars.chandra.paksha} • {threePillars.chandra.nakshatra} (प/P{threePillars.chandra.pada})
                </p>
              </div>
              <p className="text-xs text-astro-text/90 leading-relaxed border-t border-astro-border/50 pt-2">
                {threePillars.chandra.mindVerdict}
              </p>
            </div>

            {/* Sun (Surya) */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-4 shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  {isHi ? '३. सूर्य (आत्मा व सत्ता)' : '3. Surya (Soul, Will & Rank)'}
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1 ${threePillars.surya.element.color}`}>
                  {getElementIcon(threePillars.surya.element.en)}
                  {isHi ? threePillars.surya.element.hi : threePillars.surya.element.en}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-astro-primary font-serif">
                  {threePillars.surya.sign} ({threePillars.surya.degreeFormatted})
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi ? 'स्वामी:' : 'Lord:'} <strong>{threePillars.surya.lord}</strong> • {threePillars.surya.nakshatra} (प/P{threePillars.surya.pada})
                </p>
              </div>
              <p className="text-xs text-astro-text/90 leading-relaxed border-t border-astro-border/50 pt-2">
                {threePillars.surya.soulVerdict}
              </p>
            </div>
          </div>

          {/* Functional Nature Matrix for Ascendant */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-astro-text">
                  {isHi
                    ? `${threePillars.lagna.sign} लग्न के लिए ग्रहों की कार्यात्मक प्रकृति (Functional Roles)`
                    : `Planetary Functional Roles for ${threePillars.lagna.sign} Ascendant`}
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'महर्षि पाराशर का नियम: त्रिकोणेश सदैव शुभ होते हैं, केंद्रेश की प्रकृति बदलती है, और २/७ मारक होते हैं।'
                    : 'Parashari rule: Trikona lords are always auspicious; Kendra lords lose neutrality; 2nd & 7th are Marakas.'}
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-astro-primary/10 text-astro-primary">
                {kendraTrikonaScore.summaryEn}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
              {functionalClassification.map((fc) => (
                <div key={fc.planet} className="bg-astro-bg border border-astro-border rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-astro-primary">{fc.planet}</span>
                    {getRoleBadge(fc.role, fc.isBadhakesh)}
                  </div>
                  <div className="text-[11px] text-astro-secondary-text">
                    <span>{isHi ? 'भाव स्वामित्व:' : 'Houses:'} {fc.housesOwned.join(', ') || 'N/A'}</span>
                    <span className="mx-1">•</span>
                    <span>{fc.currentSign} (H{fc.currentHouse})</span>
                  </div>
                  <p className="text-[11px] text-astro-text leading-tight">{fc.summary}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Vulnerabilities & Auspicious Marks */}
          {(vulnerabilities.length > 0 || auspiciousMarks.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vulnerabilities */}
              <div className="bg-astro-card border border-rose-500/20 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400">
                    {isHi ? 'विशेष दोष एवं संवेदनशील बिंदु (Cusps / Mrityu Bhaga)' : 'Vulnerabilities & Cusp Points'}
                  </h4>
                </div>
                {vulnerabilities.length === 0 ? (
                  <p className="text-xs text-astro-secondary-text">
                    {isHi ? 'कोई गंभीर गंडांत या मृत्यु भाग नहीं मिला।' : 'No critical Gandanta or Mrityu Bhaga degrees afflicted.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {vulnerabilities.map((v, i) => (
                      <div key={i} className="bg-rose-50/50 dark:bg-rose-950/20 border border-rose-500/20 rounded-xl p-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-xs text-rose-900 dark:text-rose-200">{isHi ? v.titleHi : v.titleEn}</span>
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-rose-500/20 text-rose-700 dark:text-rose-300">
                            {v.severity}
                          </span>
                        </div>
                        <p className="text-xs text-astro-text leading-relaxed">{isHi ? v.descriptionHi : v.descriptionEn}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Auspicious Marks */}
              <div className="bg-astro-card border border-emerald-500/20 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-500" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                    {isHi ? 'विशेष मांगलिक चिन्ह (Pushkara / Vargottama)' : 'Auspicious Dignities & Pushkara'}
                  </h4>
                </div>
                {auspiciousMarks.length === 0 ? (
                  <p className="text-xs text-astro-secondary-text">
                    {isHi ? 'कोई विशिष्ट पुष्कर भाग ग्रह नहीं।' : 'No exact Pushkara Bhaga placements detected.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {auspiciousMarks.map((a, i) => (
                      <div key={i} className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-500/20 rounded-xl p-3 space-y-1">
                        <span className="font-bold text-xs text-emerald-900 dark:text-emerald-200">{isHi ? a.titleHi : a.titleEn}</span>
                        <p className="text-xs text-astro-text leading-relaxed">{isHi ? a.descriptionHi : a.descriptionEn}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* =================================================================== */}
      {/* PHASE 2: STRUCTURAL DEPTH & KARMIC BLUEPRINT (Jaimini & Kartari)   */}
      {/* =================================================================== */}
      {(activePhase === 'all' || activePhase === 'phase2') && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-astro-border pb-2">
            <Layers className="w-5 h-5 text-astro-accent" />
            <h3 className="font-serif font-bold text-lg text-astro-text">
              {isHi ? 'द्वितीय चरण: जैमिनी चर कारक व कर्तरी योग' : 'Phase 2: Jaimini Chara Karakas & Kartari Hemming'}
            </h3>
          </div>

          {/* Jaimini Chara Karakas Deck */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h4 className="text-sm font-bold text-astro-text">
                  {isHi ? 'जैमिनी सप्त चर कारक (अंश बल अनुसार)' : '7 Jaimini Chara Karakas (Degree Rank)'}
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'सर्वोच्च अंश वाला ग्रह आत्मकारक (राजा) होता है, जो आत्मा के उद्देश्य को दर्शाता है।'
                    : 'The highest degree planet is Atmakaraka (Soul King), followed by Amatyakaraka (Career).'}
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-xl text-xs text-amber-900 dark:text-amber-200">
                <strong>{isHi ? 'कारकांश लग्न:' : 'Karakamsha Lagna:'}</strong> {jaiminiKarakas.karakamshaSign} (H{jaiminiKarakas.karakamshaHouse})
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
              {/* AK */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                  AK (Soul)
                </span>
                <div className="font-bold text-base text-amber-900 dark:text-amber-100">{jaiminiKarakas.atmakaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.atmakaraka.degree.toFixed(2)}° {jaiminiKarakas.atmakaraka.sign}</div>
              </div>

              {/* AmK */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  AmK (Career)
                </span>
                <div className="font-bold text-base text-astro-primary">{jaiminiKarakas.amatyakaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.amatyakaraka.degree.toFixed(2)}° {jaiminiKarakas.amatyakaraka.sign}</div>
              </div>

              {/* BK */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  BK (Guru/Bro)
                </span>
                <div className="font-bold text-base text-astro-primary">{jaiminiKarakas.bhratrikaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.bhratrikaraka.degree.toFixed(2)}° {jaiminiKarakas.bhratrikaraka.sign}</div>
              </div>

              {/* MK */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  MK (Mother)
                </span>
                <div className="font-bold text-base text-astro-primary">{jaiminiKarakas.matrikaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.matrikaraka.degree.toFixed(2)}° {jaiminiKarakas.matrikaraka.sign}</div>
              </div>

              {/* PK */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  PK (Children)
                </span>
                <div className="font-bold text-base text-astro-primary">{jaiminiKarakas.putrakaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.putrakaraka.degree.toFixed(2)}° {jaiminiKarakas.putrakaraka.sign}</div>
              </div>

              {/* GK */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  GK (Rivals)
                </span>
                <div className="font-bold text-base text-astro-primary">{jaiminiKarakas.gnatikaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.gnatikaraka.degree.toFixed(2)}° {jaiminiKarakas.gnatikaraka.sign}</div>
              </div>

              {/* DK */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 text-center space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  DK (Spouse)
                </span>
                <div className="font-bold text-base text-astro-primary">{jaiminiKarakas.darakaraka.planet}</div>
                <div className="text-[10px] text-astro-secondary-text">{jaiminiKarakas.darakaraka.degree.toFixed(2)}° {jaiminiKarakas.darakaraka.sign}</div>
              </div>
            </div>

            <p className="text-xs text-astro-secondary-text border-t border-astro-border/60 pt-2">
              {isHi ? jaiminiKarakas.spiritualFocusHi : jaiminiKarakas.spiritualFocusEn}
            </p>
          </div>

          {/* Neecha Bhanga Raja Yoga + Kartari */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Neecha Bhanga Card */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-astro-accent" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                  {isHi ? 'नीच भंग राजयोग परीक्षण (NBRY Score)' : 'Neecha Bhanga Raja Yoga (NBRY)'}
                </h4>
              </div>

              {neechaBhanga.length === 0 ? (
                <div className="p-3 bg-astro-bg border border-astro-border rounded-xl text-xs text-astro-secondary-text">
                  {isHi ? 'कुंडली में कोई नीच ग्रह नहीं है। सभी ग्रह सामान्य स्थिति में हैं।' : 'No debilitated planets found in the D1 chart.'}
                </div>
              ) : (
                <div className="space-y-2.5">
                  {neechaBhanga.map((nb, i) => (
                    <div key={i} className="bg-astro-bg border border-astro-border rounded-xl p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs text-astro-primary">
                          {nb.planet} in {nb.debilitatedSign} (H{nb.house})
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${nb.isCancelled ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/20 text-rose-700 dark:text-rose-300'}`}>
                          {nb.isCancelled ? `NBRY Active (${nb.score}/4)` : 'Uncancelled'}
                        </span>
                      </div>
                      <p className="text-xs text-astro-text">{isHi ? nb.verdictHi : nb.verdictEn}</p>
                      {nb.conditionsMet.length > 0 && (
                        <div className="text-[10px] text-astro-secondary-text space-y-0.5 border-t border-astro-border/40 pt-1">
                          {nb.conditionsMet.map((c, ci) => (
                            <div key={ci} className="flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                              <span>{c}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kartari Hemming Card */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-astro-accent" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                  {isHi ? 'कर्तरी योग (कैंची प्रभाव - बंधन व सुरक्षा)' : 'Kartari Yoga (Hemming Scissor Status)'}
                </h4>
              </div>

              <div className="space-y-2">
                {kartari.map((k, i) => (
                  <div key={i} className="bg-astro-bg border border-astro-border rounded-xl p-2.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-astro-text">{k.target}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                        k.type === 'papa_kartari'
                          ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300'
                          : k.type === 'shubha_kartari'
                          ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                          : 'bg-slate-500/15 text-astro-secondary-text'
                      }`}>
                        {k.type === 'papa_kartari' ? 'Papa Kartari' : k.type === 'shubha_kartari' ? 'Shubha Kartari' : 'Open'}
                      </span>
                    </div>
                    <p className="text-xs text-astro-secondary-text leading-tight">{isHi ? k.verdictHi : k.verdictEn}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* =================================================================== */}
      {/* PHASE 3: MICROSCOPIC VERIFICATION (D1 to D9 Shift & Wealth Lagnas) */}
      {/* =================================================================== */}
      {(activePhase === 'all' || activePhase === 'phase3') && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-astro-border pb-2">
            <Activity className="w-5 h-5 text-astro-accent" />
            <h3 className="font-serif font-bold text-lg text-astro-text">
              {isHi ? 'तृतीय चरण: नवांश पुष्टि (D1 ➔ D9) व विशेष धन लग्न' : 'Phase 3: Navamsha Shift & Special Prosperity Lagnas'}
            </h3>
          </div>

          {/* D1 to D9 Shift Table */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-astro-text">
                  {isHi ? 'D1 बनाम D9 ग्रह स्थिति परिवर्तन (D1 Promises, D9 Delivers)' : 'D1 vs. D9 Planetary Dignity Shift'}
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'यदि ग्रह D1 में उच्च है किंतु D9 में नीच हो तो प्रारंभिक चमक के बाद निराशा मिलती है।'
                    : 'A planet exalted in D1 but debilitated in D9 gives false hope; debilitated in D1 but exalted in D9 is a late bloomer.'}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] tracking-wider">
                    <th className="py-2.5 px-3">{isHi ? 'ग्रह' : 'Planet'}</th>
                    <th className="py-2.5 px-3">D1 Rashi (H)</th>
                    <th className="py-2.5 px-3">D9 Rashi (H)</th>
                    <th className="py-2.5 px-3">{isHi ? 'रूपांतरण स्थिति' : 'Shift Verdict'}</th>
                    <th className="py-2.5 px-3">{isHi ? 'व्याख्या' : 'Interpretation'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border/50">
                  {d1toD9Shifts.map((s) => (
                    <tr key={s.planet} className="hover:bg-astro-bg/50 transition-colors">
                      <td className="py-2.5 px-3 font-bold text-astro-primary">{s.planet}</td>
                      <td className="py-2.5 px-3">
                        <span className="font-medium">{s.d1Sign}</span> <span className="text-[11px] text-astro-secondary-text">({s.d1Dignity})</span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="font-medium">{s.d9Sign}</span> <span className="text-[11px] text-astro-secondary-text">({s.d9Dignity})</span>
                      </td>
                      <td className="py-2.5 px-3">
                        {s.shiftVerdict === 'vargottama' ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300">
                            Vargottama
                          </span>
                        ) : s.shiftVerdict === 'exalted_to_debilitated' ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-700 dark:text-rose-300">
                            False Promise
                          </span>
                        ) : s.shiftVerdict === 'debilitated_to_exalted' ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                            Late Bloomer
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-500/10 text-astro-secondary-text">
                            {s.shiftVerdict}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-astro-secondary-text max-w-xs truncate sm:max-w-md sm:whitespace-normal">
                        {isHi ? s.interpretationHi : s.interpretationEn}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Special Lagnas: Indu Lagna (Wealth) & Upapada Lagna (Marriage) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Indu Lagna */}
            <div className="bg-astro-card border border-amber-500/30 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-600" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-200">
                    {isHi ? 'इन्दु लग्न (धन संपत्ति सीमा)' : 'Indu Lagna (Wealth Prosperity Ceiling)'}
                  </h4>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-800 dark:text-amber-200">
                  {specialLagnas.induLagna.wealthCeilingScore}/10
                </span>
              </div>

              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-astro-secondary-text">{isHi ? 'इन्दु लग्न राशि:' : 'Indu Lagna Sign:'}</span>
                  <strong className="text-astro-primary">{specialLagnas.induLagna.sign} (House {specialLagnas.induLagna.houseFromLagna})</strong>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-astro-secondary-text">{isHi ? 'विराजमान ग्रह:' : 'Occupants:'}</span>
                  <span>{specialLagnas.induLagna.occupants.join(', ') || (isHi ? 'कोई नहीं (स्वामी दृष्ट)' : 'None')}</span>
                </div>
                <p className="text-xs text-astro-text border-t border-astro-border/50 pt-2 leading-relaxed">
                  {isHi ? specialLagnas.induLagna.verdictHi : specialLagnas.induLagna.verdictEn}
                </p>
              </div>
            </div>

            {/* Upapada Lagna (UL) */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-rose-500" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                    {isHi ? 'उपपद लग्न (UL - वैवाहिक स्थायित्व)' : 'Upapada Lagna (UL - Marital Longevity)'}
                  </h4>
                </div>
              </div>

              <div className="bg-astro-bg border border-astro-border rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-astro-secondary-text">{isHi ? 'उपपद लग्न राशि:' : 'Upapada Sign:'}</span>
                  <strong className="text-astro-primary">{specialLagnas.upapadaLagna.sign} (House {specialLagnas.upapadaLagna.houseFromLagna})</strong>
                </div>
                <p className="text-xs text-astro-text border-t border-astro-border/50 pt-2 leading-relaxed">
                  {isHi ? specialLagnas.upapadaLagna.verdictHi : specialLagnas.upapadaLagna.verdictEn}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* =================================================================== */}
      {/* PHASE 4: DYNAMIC TIMING (Double Transit & Dasha Resonance)          */}
      {/* =================================================================== */}
      {(activePhase === 'all' || activePhase === 'phase4') && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-astro-border pb-2">
            <Clock className="w-5 h-5 text-astro-accent" />
            <h3 className="font-serif font-bold text-lg text-astro-text">
              {isHi ? 'चतुर्थ चरण: शनि-गुरु का संयुक्त गोचर एवं दशा संगति' : 'Phase 4: Saturn-Jupiter Double Transit & Dasha Resonance'}
            </h3>
          </div>

          {/* Dasha Resonance Header */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-astro-accent" />
                <h4 className="text-sm font-bold text-astro-text">
                  {isHi
                    ? `सक्रिय दशा: ${activeDashaResonance.mahadasha}-${activeDashaResonance.antardasha}`
                    : `Active Dasha: ${activeDashaResonance.mahadasha}-${activeDashaResonance.antardasha}`}
                </h4>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                activeDashaResonance.alignmentWithDoubleTransit
                  ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                  : 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
              }`}>
                {activeDashaResonance.alignmentWithDoubleTransit
                  ? (isHi ? 'दशा-गोचर पूर्ण संगति' : 'Resonance Synchronized')
                  : (isHi ? 'स्वतंत्र क्रियाशील' : 'Independent Operation')}
              </span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi ? activeDashaResonance.operationalAdviceHi : activeDashaResonance.operationalAdviceEn}
            </p>
          </div>

          {/* Double Transit Radar (K.N. Rao Formula) */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-4">
            <div>
              <h4 className="text-sm font-bold text-astro-text">
                {isHi
                  ? 'शनि व देवगुरु बृहस्पति की संयुक्त दृष्टि (K.N. Rao Double Transit Radar)'
                  : 'Saturn & Jupiter Simultaneous Double Transit Trigger'}
              </h4>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'जीवन की कोई भी बड़ी घटना (विवाह, पदोन्नति, संतान, गृह निर्माण) तभी घटती है जब शनि और गुरु दोनों उस भाव को दृष्टि या युति दें।'
                  : 'Classical rule: Major milestones only manifest when Saturn (the taskmaster) and Jupiter (the sanctifier) jointly illuminate a house.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {doubleTransitRadar.map((dt) => {
                const isActive = dt.isSimultaneouslyActivated;
                return (
                  <div
                    key={dt.houseNumber}
                    className={`rounded-2xl p-3.5 border transition-all ${
                      isActive
                        ? 'bg-amber-500/10 border-amber-500/40 ring-1 ring-amber-500/30 shadow-xs'
                        : 'bg-astro-bg border-astro-border opacity-75'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-bold text-xs text-astro-primary">
                        House {dt.houseNumber} ({dt.sign})
                      </span>
                      {isActive ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-white uppercase tracking-wider animate-pulse">
                          {isHi ? 'सक्रिय घटना' : 'Active Trigger'}
                        </span>
                      ) : (
                        <span className="text-[10px] text-astro-secondary-text">Dormant</span>
                      )}
                    </div>

                    <div className="text-xs font-semibold text-astro-text mb-1">
                      {isHi ? dt.significanceHi : dt.significanceEn}
                    </div>

                    <div className="text-[11px] text-astro-secondary-text space-y-0.5 mb-2">
                      <div>Saturn: <span className="font-mono">{dt.saturnInfluence}</span></div>
                      <div>Jupiter: <span className="font-mono">{dt.jupiterInfluence}</span></div>
                    </div>

                    <p className={`text-[11px] leading-relaxed ${isActive ? 'text-amber-950 dark:text-amber-100 font-medium' : 'text-astro-secondary-text'}`}>
                      {isHi ? dt.practicalTimingVerdictHi : dt.practicalTimingVerdictEn}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Karma Diagnostic & Remedial Strategy */}
          <div className="bg-radial from-astro-primary/10 via-astro-card to-astro-bg border border-astro-border rounded-2xl p-5 shadow-2xs space-y-2">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-astro-accent" />
              <h4 className="font-serif font-bold text-sm text-astro-primary">
                {isHi ? karmaDiagnostic.titleHi : karmaDiagnostic.titleEn}
              </h4>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi ? karmaDiagnostic.prescriptionHi : karmaDiagnostic.prescriptionEn}
            </p>
          </div>
        </section>
      )}
    </div>
  );
};
